import React, { useState } from 'react';
import axios from 'axios';

const API_BASE = '/api';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '2px solid #e5e7eb',
  fontSize: 15,
  outline: 'none',
  transition: 'border 0.2s',
  fontFamily: "'Heebo', sans-serif",
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const ContactUsPage: React.FC = () => {
  const [form, setForm] = useState({
    orgName: '',
    registrationNumber: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactRole: '',
    address: '',
    city: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#7c3aed';
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#e5e7eb';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.orgName || !form.contactName || !form.contactEmail) {
      setError('יש למלא שם עמותה, שם איש קשר ואימייל');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/leads/public`, form);
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בשליחה');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Heebo', sans-serif",
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 520,
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                padding: '40px 24px',
                color: '#fff',
              }}
            >
              <div style={{ fontSize: 64, marginBottom: 12 }}>&#10003;</div>
              <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>תודה!</h1>
              <p style={{ fontSize: 16, opacity: 0.9, marginTop: 8 }}>
                ניצור אתכם קשר בהקדם
              </p>
            </div>
            <div style={{ padding: '32px 24px' }}>
              <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7 }}>
                הפנייה שלכם התקבלה בהצלחה. צוות Amutech ייצור אתכם קשר תוך 24 שעות.
              </p>
              <a
                href="/"
                style={{
                  display: 'inline-block',
                  marginTop: 16,
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: '#fff',
                  borderRadius: 12,
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                חזרה לדף הבית
              </a>
            </div>
            <div
              style={{
                padding: '16px 24px',
                background: '#f9fafb',
                borderTop: '1px solid #f3f4f6',
                fontSize: 13,
                color: '#9ca3af',
              }}
            >
              Amutech — מערכת ניהול לעמותות
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Heebo', sans-serif",
        padding: '24px 0',
      }}
    >
      <div style={{ width: '100%', maxWidth: 680, padding: '0 12px' }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 24,
            boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
              padding: '32px 24px',
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: 1,
                marginBottom: 4,
              }}
            >
              Amutech
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '8px 0 0' }}>
              צרו קשר — נשמח לעזור!
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
            <div
              className="grid grid-cols-1 sm:grid-cols-2"
              style={{
                gap: 16,
              }}
            >
              {/* Right column - org details */}
              <div>
                <h3
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#7c3aed',
                    marginBottom: 14,
                    marginTop: 0,
                  }}
                >
                  פרטי העמותה
                </h3>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>שם עמותה *</label>
                  <input
                    type="text"
                    value={form.orgName}
                    onChange={set('orgName')}
                    placeholder="שם העמותה"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>מספר רישום</label>
                  <input
                    type="text"
                    value={form.registrationNumber}
                    onChange={set('registrationNumber')}
                    placeholder="מספר עמותה"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>כתובת</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={set('address')}
                    placeholder="כתובת"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>עיר</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={set('city')}
                    placeholder="עיר"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
              </div>

              {/* Left column - contact details */}
              <div>
                <h3
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#7c3aed',
                    marginBottom: 14,
                    marginTop: 0,
                  }}
                >
                  איש קשר
                </h3>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>שם איש קשר *</label>
                  <input
                    type="text"
                    value={form.contactName}
                    onChange={set('contactName')}
                    placeholder="שם מלא"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>אימייל *</label>
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={set('contactEmail')}
                    placeholder="email@example.com"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    dir="ltr"
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>טלפון</label>
                  <input
                    type="tel"
                    value={form.contactPhone}
                    onChange={set('contactPhone')}
                    placeholder="050-0000000"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    dir="ltr"
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>תפקיד</label>
                  <input
                    type="text"
                    value={form.contactRole}
                    onChange={set('contactRole')}
                    placeholder="למשל: מנכ״ל, רכז מתנדבים"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div
                style={{
                  background: '#fef2f2',
                  color: '#dc2626',
                  padding: '10px 16px',
                  borderRadius: 10,
                  fontSize: 14,
                  marginBottom: 16,
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '14px',
                background: submitting
                  ? '#d1d5db'
                  : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: '#fff',
                border: 'none',
                borderRadius: 14,
                fontSize: 17,
                fontWeight: 800,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: "'Heebo', sans-serif",
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: submitting
                  ? 'none'
                  : '0 4px 14px rgba(124,58,237,0.3)',
              }}
              onMouseEnter={e => {
                if (!submitting) e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {submitting ? 'שולח...' : 'שלחו פנייה'}
            </button>
          </form>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              background: '#f9fafb',
              borderTop: '1px solid #f3f4f6',
              textAlign: 'center',
              fontSize: 13,
              color: '#9ca3af',
            }}
          >
            Amutech — מערכת ניהול לעמותות
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
